import { useRef, ReactNode } from "react";
import { Button } from "react-bootstrap";
import usageAnalyticsClient from "@/apis/usageAnalyticsClient";
import { reverseTranslate } from "@/hooks/useLanguage";

export interface SntButtonProps {
  variant?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  icon?: ReactNode;
  text?: string;
  title?: string;
  trackingKey?: string;
  className?: string;
  style?: React.CSSProperties;
  size?: "sm" | "lg";
  disabled?: boolean;
  setDisabled?: (disabled: boolean) => void;
  children?: ReactNode;
}

const SntButton: React.FC<SntButtonProps> = ({
  variant = "sensolus-greylight",
  onClick,
  icon,
  text = "",
  title = "",
  trackingKey,
  className = "",
  style = {},
  size,
  disabled = false,
  setDisabled,
  children,
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return;

    e.persist(); // Prevent event from being returned to the pool
    e.preventDefault();
    e.stopPropagation();

    if (disabled) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const iconName = icon && typeof icon === "object" && "type" in icon ? ((icon as any).type?.displayName || (icon as any).type?.name || null) : null;
    const resolvedKey = trackingKey || reverseTranslate(title) || reverseTranslate(text);
    const label = resolvedKey || title || text || iconName;
    if (label) {
      usageAnalyticsClient.trackCustomEvent("button-click", {
        button: label,
        ...(resolvedKey && { text: title || text }),
      });
    }

    if (setDisabled) {
      setDisabled(true);
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      onClick(e);
    });
  };

  return (
    <Button
      variant={variant}
      className={`${className}`}
      title={title || text}
      style={style}
      size={size}
      disabled={disabled}
      onClick={(e) => {
        handleClick(e);
      }}
    >
      <div className="d-flex align-items-center justify-content-center">
        <span style={{ lineHeight: "1.5" }}>{icon ? icon : ""}</span>
        {(text || children) && (
          <span className={`${icon ? "ms-1 d-none" : ""} d-lg-inline-block`}>
            {text || children}
          </span>
        )}
      </div>
    </Button>
  );
};

export default SntButton;
